import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Popover, Tag, Tooltip } from 'antd';
import { Icon, Flex, Span, Link } from 'ui';
import styled from 'styled-components';
import { injectIntl } from 'react-intl';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { technichianSkillsSortOptions } from '../../../../../utils/app-configs';

const StyledFlex = styled(Flex)`
  color: ${p => p.theme.colors.text};
  gap: 3px;
  width: fit-content;
  cursor: pointer;
`;

const StyledIcon = styled(Icon)`
  color: ${p => p.theme.colors.text};
  ${p => p.float && `
    float: right;
    padding-top: 5px;
  `}
`;

const SkillsLink = styled(Span)`
  font-size: 12px;
  text-decoration: underline;
`;

const SortingOption = styled(Link)`
    text-decoration: ${p => p.selected ? 'none' : 'underline'};
    margin: 0 5px;

    ${p => p.selected && `
      color: ${p.theme.colors.text};
      text-decoration: none;
      cursor: default;
      &:hover {
        color: ${p.theme.colors.text};
      }
    `}
`;

const TagWrapper = styled.div`
    display: flex;
    flex-flow: row wrap;
    row-gap: 5px;
    max-width: 350px;
`;

const StyledTag = styled(Tag)`
  text-align: center;
  cursor: default;
`;

const TechnicianSkills = ({
  assignedSkills = [],
  intl,
  isOpen,
  setIsPopoverOpen,
  laneId,
}) => {
  const [selectedSortOption, setSelectedSortOption] =
    useState(technichianSkillsSortOptions.level.option);
  const [sortedSkills, setSortedSkills] = useState([]);

  useDeepCompareEffect(() => {
    setSortedSkills(assignedSkills?.sort(technichianSkillsSortOptions.level.sorting));
  }, [assignedSkills]);

  const handleReset = () => {
    setSelectedSortOption(technichianSkillsSortOptions.level.option);
    setSortedSkills(assignedSkills?.sort(technichianSkillsSortOptions.level.sorting));
  };

  const handleSortingOptionSelect = (sortOption) => {
    setSelectedSortOption(sortOption);
    setSortedSkills(assignedSkills?.sort(technichianSkillsSortOptions[sortOption].sorting));
  };

  return (
    <>
      <Popover
        getPopupContainer={triggerNode => triggerNode.parentNode.parentNode}
        placement='bottomLeft'
        trigger='click'
        open={isOpen}
        destroyTooltipOnHide={true}
        onOpenChange={handleReset}
        content={
          <>
            <StyledIcon
              type='xmark'
              size='sm'
              onClick={() => setIsPopoverOpen(null)}
              float={true}
            />
            <Flex aic marginBottom={5}>
              <span>
                {intl.formatMessage({ id: 'planningBoard.technichianSkills.sorting.TITLE' })}:
              </span>
              <SortingOption
                onClick={() => handleSortingOptionSelect(technichianSkillsSortOptions.level.option)}
                selected={selectedSortOption === technichianSkillsSortOptions.level.option}
              >
                {intl.formatMessage({
                  id: 'planningBoard.technichianSkills.sorting.LEVEL',
                })}
              </SortingOption>
              |
              <SortingOption
                onClick={() => handleSortingOptionSelect(technichianSkillsSortOptions.skill.option)}
                selected={selectedSortOption === technichianSkillsSortOptions.skill.option}
              >
                {intl.formatMessage({
                  id: 'planningBoard.technichianSkills.sorting.SKILL',
                })}
              </SortingOption>
            </Flex>
            <TagWrapper>
              {sortedSkills?.map(skill =>
                <Tooltip
                  key={`tooltip-${skill.id}`}
                  placement='topLeft'
                  autoAdjustOverflow={false}
                  mouseEnterDelay={0.5}
                  getPopupContainer={node => node.parentNode}
                  title={`${skill.description}: Level ${skill.level}`}
                >
                  <StyledTag
                    key={skill.id}
                    color={'blue'}
                  >
                    {`${skill.name} ${skill.level}`}
                  </StyledTag>
                </Tooltip>,
              )
              }
            </TagWrapper>
          </>
        }
      >
        <StyledFlex aic onClick={() => setIsPopoverOpen(isOpen ? null : laneId)}>
          <StyledIcon type='award' size='2xs' />
          <SkillsLink>
            {intl.formatMessage({ id: 'planningBoard.technichianSkills.SKILLS_TITLE' })}
          </SkillsLink>
          <StyledIcon type='chevron-down' size='2xs' />
        </StyledFlex>
      </Popover>
    </>
  );
};

TechnicianSkills.propTypes = {
  intl: PropTypes.object,
  assignedSkills: PropTypes.array,
  isOpen: PropTypes.bool,
  setIsPopoverOpen: PropTypes.func,
  laneId: PropTypes.string,
};

export default injectIntl(TechnicianSkills);
